import type { Control } from 'react-hook-form';

import { useCallback, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Invoice } from '~/api/invoices/types';

import {
  useCreateInvoiceAttachment,
  useDeleteInvoiceAttachment,
  useInvoiceAttachment,
  useInvoiceCalculationData,
  useUpdateDraftInvoice,
} from '~/api/invoices';
import { groupLinesInFormSections } from '~/features/documents';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import {
  useDefaultBrandId,
  useDefaultClientAndContactId,
  useDefaultCondition,
  useDefaultCustomAgreement,
  useDefaultCustomCondition,
  useDefaultNumberingSeriesId,
  useDefaultSpecialConditions,
} from '~/hooks/useDefaultFormValues';
import { toast } from '~/utils/toast';

import type { CreateEditInvoiceFormType } from './types';

import { mapInvoiceFormDataToDraftPayload } from '../invoices.utils';

export const useInvoiceDefaultValues = (invoice: Invoice): CreateEditInvoiceFormType => {
  const brandId = useDefaultBrandId(invoice.brandId);
  const numberingSeriesId = useDefaultNumberingSeriesId(invoice.numberingSeriesId, 'invoice');
  const { clientId, contactId } = useDefaultClientAndContactId(invoice.clientId, invoice.contactId);
  const condition = useDefaultCondition(invoice.conditionId);
  const customCondition = useDefaultCustomCondition(invoice.customConditionId);
  const customAgreement = useDefaultCustomAgreement(invoice.customAgreementId, contactId);
  const specialConditions = useDefaultSpecialConditions(invoice.specialConditions, contactId, customAgreement?.id ?? null);

  return {
    brandId,
    clientId,
    contactId,
    numberingSeriesId,
    title: invoice.title ?? '',
    description: invoice.description ?? '',
    specialConditions,
    clientReference: invoice.clientReference ?? '',
    userComments: invoice.userComments ?? '',
    condition: customAgreement ?? customCondition ?? condition,
    paymentTerm: invoice.paymentTerm ?? 30,
    paymentTermEndOfMonth: invoice.paymentTermEndOfMonth,
    sections: groupLinesInFormSections(invoice.invoiceLines),
  };
};

/**
 * Save the draft invoice when input changes (debounced)
 */
export const useAutoSave = (id: number, control: Control<CreateEditInvoiceFormType>, isEnabled = true) => {
  const { isPending, mutate: updateDraftInvoice } = useUpdateDraftInvoice(id);

  const formData = useWatch({ control }) as CreateEditInvoiceFormType;
  const [debouncedData] = useDebouncedState(formData);

  useEffect(() => {
    if (isEnabled) updateDraftInvoice(mapInvoiceFormDataToDraftPayload(debouncedData));
  }, [debouncedData, isEnabled, updateDraftInvoice]);

  return isPending;
};

/**
 * Get the invoice calculation data based on the form data
 * (Because in edit mode, there is no auto save that will update the calculation data)
 */
export const useCalculationData = (invoice: Invoice, control: Control<CreateEditInvoiceFormType>) => {
  const formData = useWatch({ control }) as CreateEditInvoiceFormType;
  const [debouncedData] = useDebouncedState(formData, 100);

  const { data, isFetching } = useInvoiceCalculationData(invoice, mapInvoiceFormDataToDraftPayload(debouncedData));

  return useMemo(
    () => ({
      calculationData: data ?? invoice.calculationData, // Fallback in case the query fails
      isFetching,
    }),
    [data, invoice.calculationData, isFetching],
  );
};

export const useAttachment = (id: number) => {
  const { data: attachment } = useInvoiceAttachment(id);
  const {
    isError: hasUploadFailed,
    isPending: isUploading,
    mutate: uploadAttachment,
    reset: resetUpload,
    variables: uploadedFile,
  } = useCreateInvoiceAttachment(id);
  const { isPending: isRemoving, mutate: deleteAttachment } = useDeleteInvoiceAttachment(id);

  const { t } = useTranslation(['invoices']);

  const select = useCallback(
    ([file]: File[]) => {
      if (file !== undefined) uploadAttachment(file);
    },
    [uploadAttachment],
  );

  const retry = useCallback(() => {
    if (uploadedFile !== undefined) uploadAttachment(uploadedFile);
  }, [uploadAttachment, uploadedFile]);

  const remove = useCallback(() => {
    // A failed upload never reached the server, so dropping the mutation is the whole removal.
    if (hasUploadFailed) return resetUpload();

    deleteAttachment(undefined, {
      // Drop the mutation too, so its file no longer describes the (now empty) attachment.
      onSuccess: () => resetUpload(),
      onError: () => toast.danger({ description: t('invoices:createEdit.attachments.error.delete') }),
    });
  }, [deleteAttachment, hasUploadFailed, resetUpload, t]);

  const open = useCallback(() => {
    if (!attachment) return;

    window.open(URL.createObjectURL(attachment), '_blank');
  }, [attachment]);

  /**
   * What there is to show, or `null` when there is nothing (the form renders the dropzone instead).
   *
   * `uploadedFile` is the selected file, held by the mutation from the moment the upload starts until
   * it is reset — which also covers the gap between a finished upload and the refetched attachment.
   */
  const file = useMemo(() => {
    if (!attachment && uploadedFile === undefined) return null;

    return {
      error: hasUploadFailed,
      isUploading,
      // The endpoint returns a bare blob, so the name is only known for a file uploaded in this session.
      name: uploadedFile?.name,
      size: uploadedFile?.size ?? attachment?.size,
    };
  }, [attachment, hasUploadFailed, isUploading, uploadedFile]);

  return useMemo(
    () => ({
      file,
      isRemoving,
      // Viewing needs the blob, which is absent while the upload is still in flight or has failed.
      open: attachment ? open : undefined,
      remove,
      retry,
      select,
    }),
    [attachment, file, isRemoving, open, remove, retry, select],
  );
};
